import { SITE } from "@/constants/site";
import conseils from "./conseils-en";

/* English version — translated from the French reference content. */
const en = {
    nav: {
        approche: "Approach",
        soins: "Treatments",
        tarifs: "Pricing",
        cabinet: "Clinic",
        medecin: "Doctor",
        faq: "FAQ",
        conseils: "Advice",
        rdvShort: "Book",
        rdvLong: "Book an appointment",
        langAria: "Choose a language",
    },
    common: {
        rdvOn: "Book an appointment on",
        viewMaps: "View on Google Maps",
        readMore: "Learn more",
        home: "Home",
        breadcrumbAria: "Breadcrumb",
        hoursTitle: "Opening hours",
        faqTitle: "Frequently asked questions",
        rdvTitle: "Book an appointment",
        conseilsLink: "Read the before and after care advice",
        closed: "Closed",
    },
    beforeAfter: {
        before: "Before",
        after: "After",
        compare: "Compare before and after:",
    },
    hours: {
        days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        range: "10.30am – 5.30pm",
        compactStrong: "Monday, Tuesday, Thursday and Friday",
        compactRest: "from 10.30am to 5.30pm — closed on Wednesday, Saturday and Sunday.",
    },
    hero: {
        eyebrow: "Dr Souhali · Physician · Asnières-sur-Seine",
        h1: "Your hormones, your skin, your hair. One single follow-up.",
        sub: "Your aesthetic medical practice in Asnières-sur-Seine, near Paris.",
        specialites:
            "Aesthetic medicine · anti-ageing · laser hair removal and electrolysis · hyaluronic acid fillers · skinboosters · collagen stimulators · peels · microneedling · acne · melasma · intimate health",
        cta: "Book an appointment",
        link: "See the treatments",
        doctolibMention: "Book online on",
        photoAlt:
            "The clinic reception: terracotta wall, golden Aurassium medallion and cream sofa",
    },
    approche: {
        eyebrow: "Our approach",
        h2: "When your hormones show on your skin",
        lead: "Before treating what you can see, we understand where it comes from.",
        paragraphes: [
            "Dr Souhali Massilia — general practitioner with training in aesthetic medicine, anti-ageing medicine and women's health — welcomes you for comprehensive care.",
            "We treat the visible effects of hormonal disorders (PCOS, menopause): acne, hair loss, excess hair growth. Hirsutism is managed at the clinic with laser and electrolysis.",
            "We also look after your intimate health, and vaginal dryness in particular.",
            "We offer laser hair removal, hyaluronic acid injections, skinboosters, collagen stimulators, chemical peels and microneedling.",
        ],
        conclusion:
            "An approach that combines medical rigour with personalised guidance and follow-up.",
        prompt: "Does this sound familiar?",
        items: [
            ["PCOS", "Acne, hair growing where it did not before (hirsutism), hair loss, irregular cycles."],
            ["Perimenopause and menopause", "Thinner, drier skin, loss of firmness, intimate discomfort."],
            ["Endometriosis", "How your hormonal treatment affects your skin and your hair."],
            ["Androgen-related disorders", "Facial and body hair, acne, hair losing density."],
            ["After a change in weight", "Skin quality and laxity after weight gain or weight loss."],
            ["Comprehensive care", "One doctor looks at your hormonal background, your skin and your hair."],
        ],
    },
    parcours: {
        eyebrow: "The pathway",
        h2: "How it works",
        lead: "Three simple steps: we talk, we examine, we decide together. Nothing is decided before the consultation.",
        photoAlt:
            "A white worktop beneath five floral anatomical plates: brain, face, lungs, heart",
        photoLegend: "The skin analysis station, beneath the anatomical plates",
        steps: [
            ["The consultation", "we talk about your symptoms, your history and what bothers you."],
            ["The work-up", "if needed, the doctor prescribes a hormonal work-up and any useful tests."],
            ["The plan", "medical follow-up, skin, hair or hair-growth treatments. We choose together."],
        ],
    },
    soinsSection: {
        eyebrow: "What we offer",
        h2: "Treatments",
        lead: "Hormonal follow-up and aesthetic medicine in the same place. Every procedure is performed or supervised by a doctor.",
        photoAlt: "The treatment room: treatment couch, laser and large windows",
        photoLegend: "The treatment room",
        familles: [
            ["Hormonal follow-up", ["PCOS and hormonal disorder consultation", "perimenopause and menopause", "assessment of hormone-related signs"], "sopk"],
            ["Laser hair removal and electrolysis", ["hair growth linked to PCOS and hirsutism", "face and body", "electrolysis for light-coloured hair", "sessions by single or multiple areas"], "epilation-laser"],
            ["Hair", ["hair loss assessment", "scalp mesotherapy"], "chute-de-cheveux"],
            ["Skin quality", ["superficial and medium peels", "mesotherapy", "microneedling", "skinbooster", "melasma and pigment spots"], "peau-et-rides"],
            ["Wrinkles and laxity", ["hyaluronic acid injections", "collagen stimulators", "collagen-stimulating threads"], "acide-hyaluronique"],
            ["Intimate health", ["vaginal dryness", "vulvovaginal atrophy"], "sante-intime"],
        ],
        detailTitle: "In detail, concern by concern",
        illustrations: {
            eyebrow: "Medical illustrations",
            h3: "What we aim for",
            lead: "Three common reasons for consulting. Drag the slider to compare before and after.",
            items: {
                levres: {
                    title: "Lips — hyaluronic acid",
                    note: "Hydration and redefined contour in a single session. Measured volume, never frozen.",
                    beforeAlt: "Illustration: thin lips with a faint contour, before injection",
                    afterAlt: "Illustration: hydrated lips with a redefined contour, after hyaluronic acid injection",
                },
                melasma: {
                    title: "Melasma — pigment spots",
                    note: "Depigmenting protocol, peels and sun protection over several months.",
                    beforeAlt: "Illustration: brown melasma patches on the cheekbones and forehead",
                    afterAlt: "Illustration: even complexion after melasma treatment",
                },
                cheveux: {
                    title: "Hair loss — density",
                    note: "Assessment of the cause, then scalp mesotherapy and long-term treatment.",
                    beforeAlt: "Illustration: widened parting and visible scalp, hair loss",
                    afterAlt: "Illustration: restored hair density, narrower parting",
                },
            },
            disclaimer:
                "Illustrative images for educational purposes. They do not depict patients of the clinic and are not a promise of results.",
        },
        footnote:
            "Results vary from one person to another. Everything is explained to you before we begin. For aesthetic procedures, a written quote is provided and a cooling-off period is observed.",
    },
    tarifsSection: {
        eyebrow: "Full transparency",
        h2: "Pricing",
        lead: "Clear prices, announced before we begin. For every aesthetic procedure, a written quote is provided, explained and agreed with you.",
        apercuTitle: "Treatments at a glance",
        apercu: [
            ["Laser hair removal", "from €30 per area"],
            ["Peels and microneedling", "€120 to €200"],
            ["Mesotherapy, hair and skin", "from €150"],
            ["Hyaluronic acid", "from €350 per syringe"],
            ["Collagen stimulators", "from €400"],
            ["Intimate health", "from €350"],
        ],
        detailLink: "See the full price list",
        note: "Aesthetic medicine procedures are not reimbursed by the French health insurance system. A cooling-off period is observed before any procedure.",
    },
    lieu: {
        eyebrow: "The place",
        h2: "The place",
        lead: "A quiet practice in Asnières-sur-Seine, bright and welcoming, designed so that you feel at ease.",
        photos: [
            ["cafe.jpg", "The coffee corner, with a terracotta arched shelf and an olive tree", "The coffee corner"],
            ["peau.jpg", "An olive tree and two terracotta frames on the wall", "The olive tree corner"],
            ["bureau.jpg", "A treatment room with its couch, its devices and the doctor's framed diplomas", "The treatment room"],
        ],
        detailLink: "See the clinic and how to get there",
    },
    medecinSection: {
        eyebrow: "The doctor",
        h2: "Dr Souhali Massilia",
        lead: "Founding physician of Aurassium Clinic.",
        photoAlt: "Portrait of Dr Souhali Massilia, physician, in medical attire",
        formationButton: "Training and qualifications",
        rpps: `RPPS no. ${SITE.rpps}`,
        detailLink: "See the full background",
    },
    formation: {
        dialogTitle: "Training",
        subtitle: `${SITE.doctor}, state-qualified physician · RPPS no. ${SITE.rpps}`,
        closeLabel: "Close",
        diplomesTitle: "National and university diplomas",
        autresTitle: "Further training",
        diplomes: [
            ["2025", "University diploma in injection and filling techniques", "Université Paris-Est Créteil Val-de-Marne (UPEC)"],
            ["2024", "University diploma in aesthetic medicine", "Université Paris-Sud (Paris 11)"],
            ["2022", "Postgraduate qualification in geriatric medicine", "Sorbonne University, Faculty of Medicine (Paris VI)"],
        ],
        autres: [
            ["2025", "Training in aesthetic oculoplastic surgery", "UPEC"],
            ["2024", "International College of Aesthetic Medicine", "Paris"],
        ],
        expertises: [
            "Female hormonal disorders",
            "Aesthetic medicine",
            "Laser hair removal",
            "Skin and hair health",
        ],
    },
    faq: {
        eyebrow: "Frequently asked questions",
        h2: "What patients ask most often",
        lead: "Answers to the questions asked before a first appointment.",
        items: [
            [
                "What is PCOS?",
                "Polycystic ovary syndrome is a common hormonal imbalance. It often shows as irregular cycles, acne, excess hair growth or hair loss. The diagnosis is based on an interview, a clinical examination and a hormonal work-up.",
            ],
            [
                "How do I know whether my skin or hair is reacting to a hormonal disorder?",
                "Acne that persists into adulthood, facial hair, hair thinning on the top of the scalp or brown patches that keep coming back: these are signs that deserve a work-up. The consultation looks for the cause before treating the sign.",
            ],
            [
                "What happens at the first consultation?",
                "It unfolds in three stages: we talk about your symptoms and your history, the doctor examines your skin, hair and hair growth, then you decide on the plan together. Nothing is decided or performed before that conversation.",
            ],
            [
                "Do I need a referral letter from my GP?",
                "No. Appointments are booked directly online on Doctolib. Please bring your most recent blood test results if you have them, along with a list of your current treatments.",
            ],
            [
                "Which treatments are available at the clinic?",
                "Follow-up of PCOS and hormonal disorders, perimenopause and menopause, hair loss assessment, laser hair removal and electrolysis of the face and body, skin quality (peels, mesotherapy, microneedling, skinbooster), melasma treatment, hyaluronic acid injections, collagen stimulators and intimate health.",
            ],
            [
                "Hirsutism: laser or electrolysis?",
                "Both are performed at the clinic, often together. Laser treats dark hair over large areas effectively; electrolysis takes over on light, white or resistant hairs, one hair at a time. The choice is made after examining the area and the hair type.",
            ],
            [
                "What do you offer for vaginal dryness?",
                "Dryness and intimate discomfort are common at the menopause or during hormonal treatment. The consultation assesses the situation and offers suitable medical care, which may be combined with a treatment at the clinic.",
            ],
            [
                "Are aesthetic medicine procedures reimbursed?",
                "No, aesthetic medicine procedures are not covered by the French health insurance system. A quote is provided and a cooling-off period is observed before any procedure.",
            ],
            [
                "Where is the clinic?",
                "At 10 rue Vladimir Kramnik, 92600 Asnières-sur-Seine, close to Clichy, Bois-Colombes, Courbevoie and Levallois-Perret.",
            ],
        ],
    },
    rdv: {
        eyebrow: "Appointments",
        h2: "Book an appointment",
        lead: "Online, on Doctolib. For a first appointment, choose “first consultation”.",
        onlineTitle: "Online",
        onlineText: "Under the name Dr Souhali, in Asnières-sur-Seine.",
        addressTitle: "Address",
    },
    footer: {
        doctorLine: `© 2026 ${SITE.name} · ${SITE.doctor}, physician`,
        rpps: `RPPS no. ${SITE.rpps}`,
        legal: "Legal notice (French)",
        privacy: "Privacy (French)",
        instagramAria: "Aurassium Clinic on Instagram",
    },
    tarifsData: {
        consultations: [
            ["Consultation", "€60", "First appointment or follow-up"],
            ["PCOS consultation", "€70", "Full hormonal work-up"],
        ],
        soinsA: [
            ["Skin quality", [
                ["Superficial peel", "€150"],
                ["Medium peel", "€200"],
                ["Mesotherapy", "€150"],
                ["Microneedling", "€120"],
                ["Skinbooster", "€300 (per session)"],
            ]],
            ["Hair", [["Scalp mesotherapy, per session", "from €150"]]],
        ],
        soinsB: [
            ["Wrinkles and laxity", [
                ["Hyaluronic acid, per syringe", "from €350"],
                ["Collagen stimulators, per session", "from €400"],
                ["Collagen-stimulating threads", "from €500"],
            ]],
            ["Intimate health", [
                ["Vaginal dryness, per session", "€350"],
                ["Vulvovaginal atrophy", "on quotation"],
            ]],
        ],
        laser: [
            ["A single area", [
                ["Between the eyebrows", "€30"],
                ["Upper lip", "€40"],
                ["Underarms", "€50"],
                ["Areolas", "€50"],
                ["Hands", "€30"],
                ["Forearms and hands", "€80"],
                ["Full arms", "€150"],
                ["Classic bikini", "€80"],
                ["High-cut bikini", "€100"],
                ["Brazilian bikini", "€120"],
                ["Full bikini", "€150"],
                ["Intergluteal area", "€50"],
                ["Lower legs, knees included", "€110"],
                ["Thighs", "€120"],
            ]],
            ["Several areas", [
                ["Underarms + forearms", "€150"],
                ["Underarms + lower legs", "€150"],
                ["Underarms + lower legs + forearms", "€200"],
                ["Underarms + full legs", "€300"],
                ["Classic bikini + lower legs + underarms", "€220"],
                ["High-cut bikini + lower legs + underarms", "€240"],
                ["Brazilian bikini + lower legs + underarms", "€260"],
                ["Full bikini + lower legs + underarms", "€280"],
                ["Intergluteal area supplement", "€40"],
            ]],
        ],
    },
    pages: {
        conseils,
        tarifs: {
            eyebrow: "Full transparency",
            h1: "Consultation and treatment prices",
            lead: "Clear prices, announced before we begin. For every aesthetic medicine procedure, a written quote is provided, explained and agreed with you, with a cooling-off period.",
            consultationsTitle: "Consultations",
            consultationsNote:
                "The consultation comes before any treatment: it is how we reach a diagnosis, check for contraindications and build the care plan.",
            soinsTitle: "Aesthetic medicine treatments",
            laserTitle: "Laser hair removal, by area",
            laserNote:
                "Price per session. The number of sessions depends on the area, the density and the colour of the hair: it is estimated during the consultation and never promised in advance.",
            note: "Aesthetic medicine procedures are not reimbursed by the French health insurance system. Medical consultations follow the usual reimbursement conditions. Prices may change: those given during the consultation prevail.",
        },
        medecin: {
            eyebrow: "The doctor",
            h1: "Dr Souhali Massilia",
            lead: "Founding physician of Aurassium Clinic, in Asnières-sur-Seine.",
            photoAlt: "Portrait of Dr Souhali Massilia, founding physician of Aurassium Clinic",
            rpps: `RPPS no. ${SITE.rpps} · registered with the Hauts-de-Seine departmental council of the French Medical Association`,
            approcheTitle: "Her approach",
            approche: [
                "General medicine, aesthetic medicine, anti-ageing medicine and women's health: this combination of training makes it possible to look at the same sign from several angles. Stubborn acne, hair growing where it did not before or hair that is thinning are not treated as isolated problems, but related to a hormonal background that is explored first.",
                "Every course of care begins with an interview and a clinical examination, followed if necessary by a work-up. The options are then set out with their limits: what a treatment can reasonably achieve, in how long, and what it will not do. Procedures are performed or supervised by the doctor.",
            ],
            jobTitle:
                "General practitioner — aesthetic medicine, anti-ageing medicine and women's health",
            ctaTitle: "Consult Dr Souhali",
            ctaText: `At the clinic, ${SITE.address}. For a first appointment, choose “first consultation”.`,
        },
        cabinet: {
            eyebrow: "The place",
            h1: "The clinic, in Asnières-sur-Seine",
            lead: "A quiet, bright and welcoming practice, designed so that you feel at ease from the moment you walk in. Consultations and treatments all take place on site, in a dedicated space.",
            photos: [
                ["accueil.jpg", "The clinic reception, with its terracotta wall and golden medallion", "Reception", "50% 50%"],
                ["cafe.jpg", "The coffee corner, with a terracotta arched shelf and an olive tree", "The coffee corner", "50% 50%"],
                ["peau.jpg", "An olive tree and two terracotta frames on the wall", "The olive tree corner", "50% 50%"],
                ["bureau.jpg", "A treatment room with its couch and the doctor's framed diplomas", "The treatment room", "50% 45%"],
                ["salle.jpg", "The treatment room equipped for laser hair removal", "The laser room", "50% 50%"],
                ["mur.jpg", "A wall of the clinic, warm and bright atmosphere", "The atmosphere", "50% 50%"],
            ],
            surPlaceTitle: "What you will find on site",
            surPlace: [
                "The clinic is organised around three spaces. First the reception, with its terracotta wall: this is where you are welcomed and where you wait, rarely for long, as appointments are spaced out to avoid queues. A coffee corner is available, with somewhere comfortable to sit.",
                "Then the consulting room, where the interview and the clinical examination take place. This is the room where we take our time: your history, your treatments, your test results, what bothers you day to day. Nothing is decided or performed before that conversation.",
                "Finally the treatment room, equipped for the procedures carried out at the clinic: laser hair removal and electrolysis, peels, mesotherapy, microneedling, injections. The equipment is medical grade and hygiene follows the standards of a medical practice, with single-use material wherever it is required.",
            ],
            derouleTitle: "What a visit looks like",
            deroule: [
                "A first visit always starts with a consultation. Its purpose is to understand where your concern comes from — a hormonal imbalance, reactive skin, hair growing where it did not before — before considering any procedure. A blood work-up may be prescribed at the end of that appointment.",
                "When an aesthetic procedure is being considered, it is never carried out straight away: a written quote is given to you and explained, and a cooling-off period is observed. You leave with the information you need to decide at home, without pressure.",
                "Follow-up sessions are shorter and focused on the agreed treatment. A review appointment is offered to assess the result and adjust what comes next. For treatments that are judged over time — hair, melasma, skin quality — photographs are taken at the clinic, under the same lighting conditions, so that the comparison is objective.",
            ],
            preparerTitle: "Preparing for your visit",
            preparerLead:
                "A few simple precautions help you get the most out of your appointment:",
            preparerItems: [
                "bring your most recent blood test results and the list of your treatments, contraception included",
                "write down your questions before you come: we go through them one by one",
                "for a laser hair removal session, shave the area the day before and avoid waxing and tweezing in the preceding weeks",
                "avoid sun exposure and self-tanners before a laser or a peel, and protect the area afterwards",
                "come without make-up if a facial treatment is planned",
                "tell us about any pregnancy, breastfeeding or ongoing treatment: some procedures are then postponed or contraindicated",
            ],
            preparerFin:
                "You are welcome to bring someone with you. If you need to cancel or reschedule, please do so on Doctolib as early as possible: the slot will be offered to another patient.",
            soinsTitle: "Treatments carried out at the clinic",
            soinsLead:
                "The Asnières-sur-Seine practice brings together two complementary activities: women's health, covering hormonal disorders and their visible effects on the skin, hair and hair growth, and aesthetic medicine, which corrects those effects once the cause is understood. The most frequent reasons for consulting are the following:",
            soinsItems: {
                "epilation-laser": "facial and body hair, hirsutism",
                "acide-hyaluronique": "lips, under-eye hollows, facial volumes",
                "peau-et-rides": "skinboosters, collagen stimulators, peels, microneedling",
                melasma: "depigmenting protocols and sun protection",
                acne: "medical management and after-effects",
                "chute-de-cheveux": "assessment, scalp mesotherapy",
                "sante-intime": "vaginal dryness and discomfort",
                sopk: "effects on skin and hair growth",
                menopause: "skin, hair, intimate comfort",
            },
            soinsFin:
                "All of these treatments are carried out on site, by the same doctor, from the first interview through to the review appointment. No procedure is delegated and none is offered without a medical indication.",
            cadreTitle: "A medical practice, not a beauty salon",
            cadre: [
                "Aurassium Clinic is a medical practice run by Dr Souhali Massilia, a physician registered with the French Medical Association. What you share during the consultation is covered by medical confidentiality, and the record created during your visit is only consulted as part of your care.",
                "In practice, this changes three things. The products used are medical devices, traced by batch number. Contraindications are systematically checked before a procedure: pregnancy, breastfeeding, photosensitising treatment, history of scarring, skin phototype. And a request may be declined or postponed when the indication is not met — an interview never commits you to a treatment.",
                "The clinic does not provide emergency care. In a medical emergency, call 15 or 112, or go to the nearest emergency department.",
            ],
            adresseTitle: "Address and access",
            adresse: [
                "The clinic is located on a quiet street in Asnières-sur-Seine, reachable by public transport as well as by car. Do allow a few minutes for parking at peak times.",
                "Please arrive a few minutes before your appointment time, with your most recent blood test results and the list of your treatments if you have them.",
            ],
            zoneTitle: "Who comes to the clinic",
            zoneLead:
                "Patients come from Asnières-sur-Seine and the neighbouring towns of the Hauts-de-Seine, as well as from north-west Paris:",
            quartiers: [
                "Asnières-sur-Seine",
                "Clichy",
                "Bois-Colombes",
                "Courbevoie",
                "Levallois-Perret",
                "Gennevilliers",
                "Colombes",
                "Paris 17th",
            ],
            faqTitle: "Frequently asked questions about the clinic",
            faq: [
                [
                    "What happens at a first appointment?",
                    "It starts with a consultation: an interview about your history and your symptoms, then a clinical examination of the skin, hair and hair growth. A work-up may be prescribed. No aesthetic procedure is carried out at that first appointment.",
                ],
                [
                    "What should I bring?",
                    "Your most recent blood test results if you have them, the list of your current treatments including contraception, and your questions. For a facial treatment, please come without make-up.",
                ],
                [
                    "Can I bring someone with me?",
                    "Yes. The reception area allows whoever comes with you to wait on site during the consultation or the treatment.",
                ],
                [
                    "Is the clinic easy to reach from neighbouring towns?",
                    "The clinic is at 10 rue Vladimir Kramnik, on a quiet street in Asnières-sur-Seine. Patients also come from Clichy, Bois-Colombes, Courbevoie, Levallois-Perret, Gennevilliers, Colombes and the 17th arrondissement of Paris.",
                ],
                [
                    "What are the opening days and hours?",
                    "The clinic sees patients by appointment on Monday, Tuesday, Thursday and Friday, from 10.30am to 5.30pm. It is closed on Wednesday, Saturday and Sunday. The slots actually available are those shown on Doctolib, which is updated in real time.",
                ],
                [
                    "How do I book an appointment?",
                    "Appointments are booked online only, through Doctolib. There you choose the reason for your visit — aesthetic medicine consultation, women's health, laser hair removal — then the slot that suits you. You receive an automatic confirmation and reminder, and you can cancel or reschedule from the same place.",
                ],
                [
                    "Are consultations and treatments reimbursed?",
                    "Aesthetic medicine procedures are not covered by the French health insurance system. Prices are announced in advance and detailed on the pricing page; for every aesthetic procedure, a written quote is provided before it is carried out.",
                ],
                [
                    "Will I need to come several times for the same treatment?",
                    "It depends on the treatment. Some procedures are done in a single session, others — laser hair removal, electrolysis, melasma, hair loss, skin quality — require several spaced sessions followed by maintenance. The expected number of sessions is given to you during the consultation, before any decision.",
                ],
            ],
            moreBefore: "To go further: the",
            moreTarifs: "pricing",
            moreMiddle:
                "page details the price of every consultation and every treatment carried out at the clinic, and the",
            moreMedecin: "doctor",
            moreAfter: "page sets out Dr Souhali Massilia's background and qualifications.",
        },
        soin: {
            faqTitle: "Frequently asked questions",
            ctaTitle: "Book an appointment",
            ctaText: `Consultation at the clinic, ${SITE.address}. For a first appointment, choose “first consultation”.`,
            relatedTitle: "Read next",
            disclaimer:
                "The information on this page is general in nature and does not replace a medical consultation. Results vary from one person to another. For aesthetic procedures, a written quote is provided and a cooling-off period is observed.",
        },
        notfound: {
            eyebrow: "Error 404",
            h1: "This page does not exist",
            lead: "The link you followed may be incomplete. Here is where to go:",
            homeLink: "Back to the home page",
        },
    },
    error: {
        h1: "Something went wrong",
        text: "This page could not be displayed properly. Reload it, or go back to the home page.",
        reload: "Reload the page",
        home: "Back to the home page",
    },
    seo: {
        home: {
            title: "Aesthetic medicine in Asnières-sur-Seine — Aurassium Clinic",
            description:
                "Dr Souhali Massilia, doctor in aesthetic medicine, anti-ageing and women's health in Asnières-sur-Seine, near Paris: laser hair removal and electrolysis, hyaluronic acid, skinboosters, peels, microneedling, PCOS and menopause.",
        },
        tarifs: {
            title: "Pricing — consultations, treatments and laser hair removal",
            description:
                "2026 price list for Dr Souhali Massilia's clinic in Asnières-sur-Seine: consultation, PCOS, peels, microneedling, skinbooster, hyaluronic acid, laser hair removal by area.",
        },
        medecin: {
            title: "Dr Souhali Massilia, doctor in Asnières-sur-Seine",
            description:
                "Dr Souhali Massilia, founding physician of Aurassium Clinic in Asnières-sur-Seine: aesthetic medicine, anti-ageing and women's health. Diplomas, training and RPPS number.",
        },
        cabinet: {
            title: "The clinic in Asnières-sur-Seine — access and photos",
            description:
                "Aurassium Clinic, 10 rue Vladimir Kramnik in Asnières-sur-Seine: photos, access, areas served and online booking.",
        },
        conseils: {
            title: "Before and after care advice — Aurassium Clinic",
            description:
                "How to prepare for laser hair removal, a peel, microneedling or injections, and what to do afterwards: precautions, sun protection and the signs that mean you should get back in touch.",
        },
        notfound: {
            title: "Page not found — Aurassium Clinic",
            description: "This page does not exist, or no longer exists.",
        },
    },
};

export default en;
